// Faturamento da plataforma (P-10).
//
// As duas telas liam `mocks/admin`: sete meses de 2026 escritos à mão e uma
// projeção com "+15%" e "churn de 5%" que ninguém calculou. Agora saem de
// `faturamento_realizado()` e `faturamento_estimado()` (migration 0027), que
// apuram sobre `assinaturas` e `faturas` — o espelho do Stripe.
//
// As funções são `security invoker` e exigem superusuário: quem não é recebe
// lista vazia, não o faturamento de outra conta.
//
// Todos os valores chegam em centavos, como o banco guarda.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface MesRealizado {
  /** Primeiro dia do mês, em ISO. */
  mes: string;
  receita: number;
  clientes: number;
  novas: number;
  cancelamentos: number;
}

export interface MesProjetado {
  mes: string;
  base: number;
  otimista: number;
  pessimista: number;
  clientes: number;
}

export interface Estimativa {
  mrr: number;
  clientes: number;
  em_teste: number;
  ticket_medio: number;
  /** Taxa mensal observada, em fração (0.15 = 15%). */
  crescimento: number;
  churn: number;
  meses_de_historico: number;
  meses: MesProjetado[];
}

function useRpc<T>(
  fn: 'faturamento_realizado' | 'faturamento_estimado',
  args: Record<string, number>,
  vazio: T,
): { dados: T; carregando: boolean; erro: string | null; recarregar: () => void } {
  const [dados, setDados] = useState<T>(vazio);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);

  // `args` é literal no ponto de chamada e recriado a cada render; serializar
  // evita refazer a consulta em todo ciclo sem obrigar quem chama a memoizar.
  const argsSerializados = JSON.stringify(args);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);

    Promise.resolve(supabase.rpc(fn, JSON.parse(argsSerializados)))
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          setErro(error.message);
          setDados(vazio);
          return;
        }
        setErro(null);
        setDados((data ?? vazio) as T);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
    // `vazio` é constante no ponto de chamada; incluí-lo refaria a consulta a
    // cada render por causa da identidade do literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, argsSerializados, versao]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  return { dados, carregando, erro, recarregar };
}

const SEM_MESES: MesRealizado[] = [];

const SEM_ESTIMATIVA: Estimativa = {
  mrr: 0,
  clientes: 0,
  em_teste: 0,
  ticket_medio: 0,
  crescimento: 0,
  churn: 0,
  meses_de_historico: 0,
  meses: [],
};

export function useFaturamentoRealizado(meses = 12) {
  const { dados, carregando, erro, recarregar } = useRpc<MesRealizado[]>(
    'faturamento_realizado',
    { p_meses: meses },
    SEM_MESES,
  );
  return { meses: dados, carregando, erro, recarregar };
}

export function useFaturamentoEstimado(meses = 5) {
  const { dados, carregando, erro, recarregar } = useRpc<Estimativa>(
    'faturamento_estimado',
    { p_meses: meses },
    SEM_ESTIMATIVA,
  );
  return { estimativa: dados, carregando, erro, recarregar };
}

/** Percentual com uma casa, a partir da fração que o banco devolve. */
export const fmtTaxa = (fracao: number) =>
  `${(fracao * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
